import {Component, HostListener, OnInit} from '@angular/core';
import {MySitesApiService} from '../../services/api/my-sites-api.service';
import {ToastrMessagesProvider} from 'src/app/services/toastr-messages.service';
import {AutoUnsubscribe} from '../../decorators/auto-unsubscribe.decorator';
import {PaymentDataService} from '../../services/payment-data.service';
import {RouterExtendedService} from '../../services/router-extended.service';
import {Location} from '@angular/common';
import {AgencyService} from '../../services/agency.service';
import {AgencyState} from '../../types/product.type';
import {AdditionalSitesService} from '../../services/add-sites.service';
import {Router} from '@angular/router';
import {
  CsvSite,
  CsvSiteList,
  ImportCsvSiteErrorItem,
  ImportCsvSiteErrorResponse,
  MultiSite,
} from '../../types/site.type';
import {Subscription} from 'rxjs';
import {ProductType} from '../../types/payment.type';
import {PluralPipe} from '../../common/pipes/plural.pipe';
import {MySitesService} from '../../services/my-sites.service';
import {SUBSCRIPTIONS} from '../../common/utils/constants';
import {UserInfoState} from '../../common/elf-store/user-info/user-info.type';
import {UserInfoQuery} from '../../common/elf-store/user-info/user-info.query';
import {AmplitudeService} from '../../services/amplitude.service';
import {AmplitudeManager} from '../../common/elf-store/amplitude/amplitude.manager';
import {SubscriptionBillingCycleType} from '../../../userway/sdk/UserWayApiClient';

const CSV_FORMAT = 'text/csv';

enum VIEW_STATES {
  ADD_SITES,
  PAYMENT_FORM,
  COMPLETE_PAYMENT,
  WIDGET_SCRIPT,
}

@Component({
  selector: 'mc-import-csv',
  templateUrl: './import-csv.component.html',
  styleUrls: ['./import-csv.component.scss'],
})
@AutoUnsubscribe()
export class ImportCsvComponent implements OnInit {
  viewStates = VIEW_STATES;
  state: VIEW_STATES = VIEW_STATES.ADD_SITES;
  loading: boolean = false;
  showBackButton = false;
  siteState: AgencyState;
  subAccountUnitId: string = null;
  /* eslint-disable-next-line local/no-subscription-variables */
  calculatedResponseSubscription: Subscription;
  userID: string;

  constructor(
    public addSitesService: AdditionalSitesService,
    public location: Location,
    private router: Router,
    private userQuery: UserInfoQuery,
    private amplitudeService: AmplitudeService,
    private amplitudeManager: AmplitudeManager,
    private agencyService: AgencyService,
    private toastr: ToastrMessagesProvider,
    private mySitesService: MySitesService,
    private mySitesApiService: MySitesApiService,
    private paymentDataService: PaymentDataService,
    private routerExtService: RouterExtendedService,
  ) {
    this.subAccountUnitId = history.state?.subAccountUnitId;
  }

  //delete subAccount id in history.state on close tab(refresh) - because it saves on refresh page
  @HostListener('window:beforeunload', ['$event']) unload() {
    history.replaceState({}, '');
  }

  ngOnInit() {
    if (this.routerExtService.getPreviousUrl().includes('/add-sites')) {
      this.showBackButton = true;
    }

    this[SUBSCRIPTIONS].add(
      this.userQuery.userInfo$.subscribe((info: UserInfoState) => {
        this.userID = info.id;
      }),
    );

    this.siteState =
      this.addSitesService.isFreeAgencyAccount || this.addSitesService.remediationAllowedFreeAndPaidStates
        ? AgencyState.FREE
        : AgencyState.PAID;
  }
  droppedCsv(data: FileList): void {
    const arrayFiles = Array.from(data);
    if (arrayFiles.length === 1) {
      const csvFile: File = arrayFiles[0];
      if (csvFile.type === CSV_FORMAT) {
        this.amplitudeManager.setPurchaseProperties({
          purchaseProductName: 'pro-site',
          purchaseTriggeredPlace: 'add-site-csv',
          purchaseTriggeredPage: this.amplitudeService.getCurrentPage(),
        });
        this.addCsvFile(csvFile);
      } else {
        this.toastr.warning('You can only upload CSV files');
        return;
      }
    } else {
      this.toastr.warning('You can upload only one CSV file');
      return;
    }
  }

  loadCsvByInput(data: Event) {
    this.droppedCsv((data.currentTarget as HTMLInputElement).files);
    (data.currentTarget as HTMLInputElement).value = null;
  }

  addCsvFile(file: File) {
    this.loading = true;
    const formData = new FormData();
    formData.append('file', file);
    if (this.agencyService.currentSubscription?.id) {
      this.mySitesApiService
        .downloadAgencyCsv(formData, this.agencyService.currentSubscription.id, {siteState: this.siteState})
        .subscribe({
          next: (res: CsvSiteList) => {
            const csvSiteList = res.payload?.sites || [];
            this.toastr.success('The upload has been started. Please note that adding websites may take some time.');
            this.mySitesService.emitSiteListReload();
            this.agencyService.getAgencySummary(this.userID);
            setTimeout(() => {
              this.loading = false;
              // this is necessary to give user some time to read  the toastr message
              this.router.navigate(['/widget/my-sites']);
              this.amplitudeService.addSite({
                addSitesTriggeredPlace: 'import-csv',
                addSiteTriggeredPage: this.amplitudeService.getCurrentPage(),
                websites: this.getHostNamesFromCsvList(csvSiteList),
              });
            }, 5000);
            this.amplitudeService.importCsv();
          },
          error: err => {
            this.handleImportCSVError(err.error);
          },
        });
    } else {
      this.mySitesApiService.downloadCsv(formData, this.subAccountUnitId).subscribe({
        next: (res: CsvSiteList) => {
          this.loading = false;
          const csvSiteList = res.payload.sites;
          const {createdSitesList, duplicateSiteList, inValidList} = csvSiteList.reduce(
            (lists, site: CsvSite) => {
              if (site.created) {
                lists.createdSitesList.push(site);
              } else if (!site.created && site.valid) {
                lists.duplicateSiteList.push(site);
              } else {
                lists.inValidList.push(site);
              }
              return lists;
            },
            {createdSitesList: [], duplicateSiteList: [], inValidList: []},
          );

          const pluralize = new PluralPipe().transform;
          const message = ` ${csvSiteList.length} ${pluralize(csvSiteList.length, 'item', 'items')} ${pluralize(
            csvSiteList.length,
            'was',
            'were',
          )} processed: ${createdSitesList.length} ${pluralize(createdSitesList.length, 'site', 'sites')} ${pluralize(
            createdSitesList.length,
            'was',
            'were',
          )} added with Free Widget, ${duplicateSiteList.length} ${pluralize(
            duplicateSiteList.length,
            'site',
            'sites',
          )} existed before, ${inValidList.length} ${pluralize(inValidList.length, 'URL', 'URLs')} ${pluralize(
            inValidList.length,
            'is',
            'are',
          )} invalid`;

          this.toastr.success(message);
          const mappedSites = createdSitesList.concat(duplicateSiteList).map(item => {
            return {
              id: item.entity.id,
              url: item.host,
            };
          });
          if (!mappedSites.length) {
            return;
          }
          this.mySitesService.emitSiteListReload();
          this.calculatePrice(mappedSites);
          this.amplitudeService.addSite({
            addSitesTriggeredPlace: 'import-csv',
            addSiteTriggeredPage: this.amplitudeService.getCurrentPage(),
            websites: this.getHostNamesFromCsvList(csvSiteList),
          });
          this.amplitudeService.importCsv();
        },
        error: err => {
          this.handleImportCSVError(err.error);
        },
      });
    }
  }

  calculatePrice(sites: MultiSite[]): void {
    this.calculatedResponseSubscription?.unsubscribe?.();
    this.calculatedResponseSubscription = this.paymentDataService.calculatedResponseSubscriber.subscribe({
      next: value => {
        if (value?.isCompletePaymentState) {
          return;
        }
        this.state = VIEW_STATES.PAYMENT_FORM;
      },
    });
    this.paymentDataService.setPaymentData(ProductType.Remediation, sites, SubscriptionBillingCycleType.ANNUAL);
  }

  openPaymentForm(): void {
    this.state = VIEW_STATES.COMPLETE_PAYMENT;
  }

  openEmbedScript() {
    this.mySitesService.emitSiteListReload();
    this.state = VIEW_STATES.WIDGET_SCRIPT;
  }

  back(step: VIEW_STATES): void {
    this.state = step;
  }

  handleImportCSVError(error: ImportCsvSiteErrorResponse): void {
    this.loading = false;
    const limitError: ImportCsvSiteErrorItem = error?.errors.find((errorItem: ImportCsvSiteErrorItem) => {
      return errorItem.errorMessage.includes('exceeds the specified maximum limit');
    });
    if (limitError) {
      const maxAllowedSize = this.extractMaxAllowedSize(limitError.errorMessage);
      this.toastr.warning(`Maximum file size limit: ${maxAllowedSize} MB.`);
    }
  }

  private getHostNamesFromCsvList(csvList: CsvSite[]): string[] {
    return csvList.map(site => site.host);
  }

  private extractMaxAllowedSize(errorMessage: string): string {
    const sizeFromMessage = errorMessage.split(':').at(-1);
    const fileSize = parseInt(sizeFromMessage, 10) || 0;
    const convertedToMbSize = fileSize / 1024 / 1024;

    return convertedToMbSize.toFixed(2);
  }
}
